import { useUploadProgressTracker } from '@/composables/useUploadProgressTracker';
import type { StatusState } from '@/types/components/status/StatusTypes';
import type { Ref } from 'vue';
import { ref } from 'vue';

type UseUploadStatusControllerReturn = {
    state: Ref<StatusState>;
    errorMessage: Ref<string | undefined>;
    displayProgress: Ref<number>;
    isShowingLoading: () => boolean;
    isShowingOffline: () => boolean;
    showLoading: () => void;
    showOffline: () => void;
    resumeLoading: () => void;
    showError: (message: string) => void;
    clear: () => void;
};

/**
 * Orchestrates the upload overlay state machine and its progress bar.
 * Encapsulates the transitions between loading, offline, and error presentations.
 */
export function useUploadStatusController(realProgress: Ref<number>): UseUploadStatusControllerReturn {
    const state = ref<StatusState>(null);
    const errorMessage = ref<string | undefined>(undefined);

    const progress = useUploadProgressTracker(realProgress);

    function showLoading(): void {
        errorMessage.value = undefined;
        state.value = 'loading';
        progress.start();
    }

    function showOffline(): void {
        state.value = 'offline';
        progress.pause();
    }

    function resumeLoading(): void {
        state.value = 'loading';
        progress.resume();
    }

    function showError(message: string): void {
        errorMessage.value = message;
        state.value = 'error';
        progress.reset();
    }

    function clear(): void {
        state.value = null;
        errorMessage.value = undefined;
        progress.reset();
    }

    function isShowingLoading(): boolean {
        return state.value === 'loading';
    }

    function isShowingOffline(): boolean {
        return state.value === 'offline';
    }

    return {
        state,
        errorMessage,
        displayProgress: progress.displayProgress,
        isShowingLoading,
        isShowingOffline,
        showLoading,
        showOffline,
        resumeLoading,
        showError,
        clear,
    };
}
